import { LandingShell } from "../components/LandingShell";
import { AppSelect, type AppSelectOption } from "../components/AppSelect";

const REGION_OPTIONS: AppSelectOption[] = [
  { value: "Europe/Paris", label: "Europe · Paris" },
  { value: "Europe/London", label: "Europe · Londres" },
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "Amériques · New York" },
  { value: "Asia/Singapore", label: "Asie · Singapour" },
];

const LANGUAGE_OPTIONS: AppSelectOption[] = [
  { value: "fr", label: "Français" },
  { value: "en", label: "Anglais" },
  { value: "es", label: "Espagnol" },
];

const IMPORT_WINDOW_OPTIONS: AppSelectOption[] = [
  { value: "night", label: "Nuit (00h00 - 06h00)" },
  { value: "morning", label: "Matin (06h00 - 12h00)" },
  { value: "afternoon", label: "Après-midi (12h00 - 18h00)" },
  { value: "evening", label: "Soir (18h00 - 00h00)" },
];

export function ConfigurationPage() {
  return (
    <LandingShell
      active="configuration"
      breadcrumb="Configuration"
      searchPlaceholder="Rechercher réglages..."
    >
      <section className="settings-hero">
        <div className="settings-hero-copy">
          <span className="settings-kicker">Prototype UI</span>
          <h1>Configuration (mock)</h1>
          <div className="settings-mock-banner" role="note" aria-label="Avertissement page mock">
            Cette page est un mock visuel non connecté. Les actions et changements ne sont pas
            enregistrés.
          </div>
          <p>
            Démonstration d’interface pour explorer des réglages types et la structure générale.
          </p>
        </div>
      </section>

      <section className="settings-grid" aria-label="Sections de configuration">
        <article className="settings-card">
          <div className="settings-card-head">
            <div>
              <h2>Profil équipe</h2>
              <p>Identité principale et timezone de référence.</p>
            </div>
            <span className="settings-pill">Démo</span>
          </div>
          <div className="settings-field">
            <label htmlFor="settings-team-name">Nom d’équipe</label>
            <input
              id="settings-team-name"
              type="text"
              defaultValue="Lainvision Ops"
              placeholder="Nom de l’équipe"
            />
          </div>
          <div className="settings-field">
            <label htmlFor="settings-region">Région principale</label>
            <AppSelect id="settings-region" defaultValue="Europe/Paris" options={REGION_OPTIONS} />
          </div>
          <div className="settings-field">
            <label htmlFor="settings-language">Langue d’interface</label>
            <AppSelect id="settings-language" defaultValue="fr" options={LANGUAGE_OPTIONS} />
          </div>
        </article>

        <article className="settings-card">
          <div className="settings-card-head">
            <div>
              <h2>Alertes & notifications</h2>
              <p>Garder l’équipe alignée sur les incidents et mises à jour.</p>
            </div>
            <span className="settings-pill settings-pill--accent">Live</span>
          </div>
          <label className="settings-switch">
            <span className="settings-switch-text">
              <strong>Alertes temps réel</strong>
              <span>Push dès qu’un capteur dépasse les seuils définis.</span>
            </span>
            <input type="checkbox" defaultChecked />
            <span className="settings-switch-track" aria-hidden="true">
              <span className="settings-switch-thumb" />
            </span>
          </label>
          <label className="settings-switch">
            <span className="settings-switch-text">
              <strong>Digest quotidien</strong>
              <span>Résumé automatique envoyé à 08h00 (heure locale).</span>
            </span>
            <input type="checkbox" defaultChecked />
            <span className="settings-switch-track" aria-hidden="true">
              <span className="settings-switch-thumb" />
            </span>
          </label>
          <label className="settings-switch">
            <span className="settings-switch-text">
              <strong>Escalade client</strong>
              <span>Notifier les contacts clients lors d’un incident critique.</span>
            </span>
            <input type="checkbox" />
            <span className="settings-switch-track" aria-hidden="true">
              <span className="settings-switch-thumb" />
            </span>
          </label>
        </article>

        <article className="settings-card">
          <div className="settings-card-head">
            <div>
              <h2>Données & stockage</h2>
              <p>Suivi rapide des quotas et des imports planifiés.</p>
            </div>
            <span className="settings-pill">72% utilisé</span>
          </div>
          <div className="settings-meter" role="progressbar" aria-valuenow={72} aria-valuemin={0} aria-valuemax={100}>
            <div className="settings-meter-bar">
              <span className="settings-meter-fill" style={{ width: "72%" }} />
            </div>
            <div className="settings-meter-row">
              <span>Stockage global</span>
              <strong>18.4 Go / 25 Go</strong>
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="settings-import-window">Fenêtre d’import</label>
            <AppSelect id="settings-import-window" defaultValue="night" options={IMPORT_WINDOW_OPTIONS} />
          </div>
          <div className="settings-inline">
            <div>
              <span className="settings-inline-label">Exports planifiés</span>
              <strong>3 workflows actifs</strong>
            </div>
            <button className="settings-link-button" type="button">
              Gérer
            </button>
          </div>
        </article>

        <article className="settings-card">
          <div className="settings-card-head">
            <div>
              <h2>Sécurité</h2>
              <p>Accès et conformité pour les comptes opérationnels.</p>
            </div>
            <span className="settings-pill settings-pill--success">OK</span>
          </div>
          <div className="settings-inline">
            <div>
              <span className="settings-inline-label">Multi-facteurs</span>
              <strong>Activé pour 6/8 comptes</strong>
            </div>
            <button className="settings-link-button" type="button">
              Voir les comptes
            </button>
          </div>
          <label className="settings-switch">
            <span className="settings-switch-text">
              <strong>Verrouillage automatique</strong>
              <span>Déconnexion après 15 min d’inactivité.</span>
            </span>
            <input type="checkbox" defaultChecked />
            <span className="settings-switch-track" aria-hidden="true">
              <span className="settings-switch-thumb" />
            </span>
          </label>
          <label className="settings-switch">
            <span className="settings-switch-text">
              <strong>Logs détaillés</strong>
              <span>Conserver 90 jours d’historique de connexion.</span>
            </span>
            <input type="checkbox" defaultChecked />
            <span className="settings-switch-track" aria-hidden="true">
              <span className="settings-switch-thumb" />
            </span>
          </label>
        </article>

        <article className="settings-card">
          <div className="settings-card-head">
            <div>
              <h2>Intégrations rapides</h2>
              <p>Brancher les outils principaux sans configuration avancée.</p>
            </div>
            <span className="settings-pill">3/5 prêtes</span>
          </div>
          <div className="settings-integration">
            <div>
              <strong>Slack</strong>
              <span>Notifications canal #ops</span>
            </div>
            <span className="settings-status settings-status--on">Connecté</span>
          </div>
          <div className="settings-integration">
            <div>
              <strong>Power BI</strong>
              <span>Refresh automatique chaque nuit</span>
            </div>
            <span className="settings-status settings-status--on">Synchronisé</span>
          </div>
          <div className="settings-integration">
            <div>
              <strong>Webhook client</strong>
              <span>À configurer (URL manquante)</span>
            </div>
            <span className="settings-status settings-status--off">En attente</span>
          </div>
          <div className="settings-integration">
            <div>
              <strong>Exports SFTP</strong>
              <span>1 flux actif</span>
            </div>
            <span className="settings-status settings-status--on">OK</span>
          </div>
        </article>
      </section>
    </LandingShell>
  );
}
